import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import pb from '../services/pocketbase';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user, isLoggedIn } = useAuth();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Mettre à jour le statut en ligne - DÉSACTIVÉ
  const updateOnlineStatus = useCallback(async (isOnline) => {
    // Désactivé temporairement pour éviter les boucles d'erreurs
    return;
    
    if (!user) return;
    
    try {
      await pb.collection('users').update(user.id, {
        is_online: isOnline,
        last_seen: new Date().toISOString()
      });
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
    }
  }, [user]);

  // Charger les chats
  const loadChats = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      let filter = '';
      if (user.role === 'admin') {
        // Admin voit TOUS les chats (toutes les conversations utilisateurs)
        filter = '';
      } else {
        // Utilisateur voit uniquement SA conversation
        filter = `user.id = "${user.id}"`;
      }
      
      const chatsList = await pb.collection('chats').getFullList({
        filter: filter,
        expand: 'user,admin',
        sort: '-updated'
      });
      
      setChats(chatsList);
      
      // Calculer le nombre total de messages non lus
      const totalUnread = chatsList.reduce((total, chat) => {
        const unreadField = user.role === 'admin' ? 'unread_count_admin' : 'unread_count_user';
        return total + (chat[unreadField] || 0);
      }, 0);
      
      setUnreadCount(totalUnread);
    } catch (error) {
      console.error('Erreur chargement chats:', error);
      // Fallback en cas d'erreur
      setChats([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Marquer les messages comme lus
  const markMessagesAsRead = useCallback(async (chatId) => {
    if (!user || !chatId) return;
    
    try {
      // Réinitialiser le compteur de messages non lus pour l'utilisateur actuel
      const unreadField = user.role === 'admin' ? 'unread_count_admin' : 'unread_count_user';
      
      await pb.collection('chats').update(chatId, {
        [unreadField]: 0
      });
      
      // Recharger les chats pour mettre à jour les compteurs
      loadChats();
    } catch (error) {
      console.error('Erreur marquage messages lus:', error);
    }
  }, [user, loadChats]);

  // Charger les messages d'un chat
  const loadMessages = useCallback(async (chatId) => {
    if (!chatId) return;
    
    try {
      setLoading(true);
      
      const messagesList = await pb.collection('chat_messages').getFullList({
        filter: `chat.id = "${chatId}"`,
        expand: 'sender',
        sort: 'created'
      });
      
      setMessages(messagesList);
      
      // Marquer les messages comme lus pour l'utilisateur actuel
      await markMessagesAsRead(chatId);
    } catch (error) {
      console.error('Erreur chargement messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [user, markMessagesAsRead]);

  // Envoyer un message
  const sendMessage = useCallback(async (content, messageType = 'text') => {
    if (!user || !activeChat || !content.trim()) return;
    
    try {
      // Créer le message
      const newMessage = await pb.collection('chat_messages').create({
        chat: activeChat.id,
        sender: user.id,
        content: content.trim(),
        message_type: messageType,
        read_status: false
      });
      
      // Mettre à jour le dernier message du chat
      await pb.collection('chats').update(activeChat.id, {
        last_message: content.trim(),
        updated: new Date().toISOString()
      });
      
      // Incrémenter le compteur de messages non lus pour l'autre utilisateur
      const otherUserField = user.role === 'admin' ? 'unread_count_user' : 'unread_count_admin';
      const currentCount = activeChat[otherUserField] || 0;
      
      await pb.collection('chats').update(activeChat.id, {
        [otherUserField]: currentCount + 1
      });
      
      // NE PAS recharger les messages - la subscription temps réel s'en charge
      // loadMessages(activeChat.id); // SUPPRIMÉ pour éviter les doublons
      loadChats(); // Garder pour mettre à jour la liste des chats
      
      return newMessage;
    } catch (error) {
      console.error('Erreur envoi message:', error);
      throw error;
    }
  }, [user, activeChat, loadChats]);

  // État pour éviter les créations multiples simultanées
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  // Créer ou récupérer un chat - GARANTIT UNE CONVERSATION UNIQUE PAR UTILISATEUR
  const getOrCreateChat = useCallback(async (targetUserId = null) => {
    if (!user) return null;
    
    // Éviter les appels simultanés
    if (isCreatingChat) {
      console.log('Création de chat déjà en cours, attente...');
      return null;
    }
    
    try {
      setIsCreatingChat(true);
      
      let existingChats = [];
      
      if (user.role === 'admin') {
        // Admin cherche la conversation avec un utilisateur spécifique
        if (!targetUserId) return null;
        existingChats = await pb.collection('chats').getFullList({
          filter: `user.id = "${targetUserId}"`,
          expand: 'user,admin'
        });
      } else {
        // Utilisateur cherche SA conversation unique (il ne peut en avoir qu'une)
        existingChats = await pb.collection('chats').getFullList({
          filter: `user.id = "${user.id}"`,
          expand: 'user,admin'
        });
      }
      
      // Si une conversation existe déjà, la retourner
      if (existingChats.length > 0) {
        return existingChats[0];
      }
      
      // Créer une nouvelle conversation UNIQUEMENT si l'utilisateur n'en a pas
      if (user.role === 'admin') {
        // Admin ne peut pas créer de conversation, seulement répondre aux existantes
        return null;
      } else {
        // Double vérification avant création pour éviter les doublons
        const doubleCheck = await pb.collection('chats').getFullList({
          filter: `user.id = "${user.id}"`,
          expand: 'user,admin'
        });
        
        if (doubleCheck.length > 0) {
          return doubleCheck[0];
        }
        
        // Utilisateur crée SA conversation unique avec le support
        const adminUsers = await pb.collection('users').getFullList({ 
          filter: 'role = "admin"' 
        });
        if (adminUsers.length === 0) {
          throw new Error('Aucun administrateur disponible');
        }
        
        // Créer la conversation unique de l'utilisateur
        const chatData = { 
          user: user.id, 
          admin: adminUsers[0].id, // Assigner au premier admin disponible
          unread_count_user: 0,
          unread_count_admin: 0
        };
        
        const newChat = await pb.collection('chats').create(chatData);
        return await pb.collection('chats').getOne(newChat.id, { expand: 'user,admin' });
      }
    } catch (error) {
      console.error('Erreur création/récupération chat:', error);
      return null;
    } finally {
      setIsCreatingChat(false);
    }
  }, [user, isCreatingChat]);

  // Ouvrir un chat
  const openChat = useCallback(async (chat) => {
    setActiveChat(chat);
    await loadMessages(chat.id);
  }, [loadMessages]);

  // Fermer le chat actif
  const closeChat = useCallback(() => {
    setActiveChat(null);
    setMessages([]);
  }, []);

  // Chargement initial et subscriptions temps réel
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    
    // Charger les chats au démarrage
    const timer = setTimeout(() => {
      loadChats();
    }, 1000);
    
    // Subscription pour les chats
    let unsubscribeChats;
    const setupChatSubscription = async () => {
      try {
        let filter = '';
        if (user.role === 'admin') {
          // Admin voit tous les chats pour pouvoir répondre à tous les utilisateurs
          filter = '';
        } else {
          filter = `user.id = "${user.id}"`;
        }
        
        unsubscribeChats = await pb.collection('chats').subscribe('*', (e) => {
          console.log('Chat update:', e);
          loadChats(); // Recharger les chats quand il y a un changement
        }, { filter });
      } catch (error) {
        console.error('Erreur subscription chats:', error);
      }
    };
    
    setupChatSubscription();
    
    return () => {
      clearTimeout(timer);
      if (unsubscribeChats) {
        pb.collection('chats').unsubscribe(unsubscribeChats);
      }
    };
  }, [isLoggedIn, user, loadChats]);

  // Subscription séparée pour les messages du chat actif (UTILISATEUR UNIQUEMENT)
  useEffect(() => {
    if (!activeChat || !user || user.role === 'admin') return;
    
    let unsubscribeMessages;
    
    const setupMessageSubscription = async () => {
      try {
        unsubscribeMessages = await pb.collection('chat_messages').subscribe('*', (e) => {
          console.log('Message update:', e);
          if (e.record.chat === activeChat.id) {
            if (e.action === 'create') {
              // Vérifier si le message n'existe pas déjà pour éviter les doublons
              setMessages(prev => {
                const messageExists = prev.some(msg => msg.id === e.record.id);
                if (messageExists) {
                  return prev; // Ne pas ajouter si déjà présent
                }
                return [...prev, e.record];
              });
              
              // Marquer comme lu si c'est notre propre message
              if (e.record.sender !== user.id) {
                markMessagesAsRead(activeChat.id);
              }
            }
          }
        }, { filter: `chat.id = "${activeChat.id}"` });
      } catch (error) {
        console.error('Erreur subscription messages:', error);
      }
    };
    
    setupMessageSubscription();
    
    return () => {
      if (unsubscribeMessages) {
        pb.collection('chat_messages').unsubscribe(unsubscribeMessages);
      }
    };
  }, [activeChat, user, markMessagesAsRead]);

  // Désactivé temporairement pour éviter les boucles d'erreurs
  // useEffect(() => {
  //   const handleBeforeUnload = () => {
  //     if (user) {
  //       updateOnlineStatus(false);
  //     }
  //   };

  //   window.addEventListener('beforeunload', handleBeforeUnload);
    
  //   return () => {
  //     window.removeEventListener('beforeunload', handleBeforeUnload);
  //   };
  // }, [user, updateOnlineStatus]);

  const value = {
    chats,
    activeChat,
    messages,
    isTyping,
    onlineUsers,
    loading,
    unreadCount,
    sendMessage,
    openChat,
    closeChat,
    getOrCreateChat,
    markMessagesAsRead,
    setIsTyping
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
